import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminDocumentTypesTable } from '../-components/AdminDocumentTypesTable';
import { CreateDocumentTypeDialog } from '../-components/CreateDocumentTypeDialog';
import { DeleteDocumentTypeDialog } from '../-components/DeleteDocumentTypeDialog';
import { DeprecateDocumentTypeDialog } from '../-components/DeprecateDocumentTypeDialog';
import { adminDocumentTypesQueryOptions } from '../-hooks/useAdminDocumentTypes';
import { useAdminDocumentTypesPage } from '../-hooks/useAdminDocumentTypesPage';

const documentTypesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/document-types/(pages)/')({
  validateSearch: documentTypesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
    // Mirrors the page hook: the deprecated/active choice lives in the table's
    // filters, and its absence means active — the same default the route's
    // querystring schema applies.
    isDeprecated: search.filters?.isDeprecated?.[0] === 'true',
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which hides the table's own
  // "Could not load" banner. Matches the other management lists.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminDocumentTypesQueryOptions(deps))
      .catch(() => undefined),
  component: DocumentTypesPage,
});

function DocumentTypesPage() {
  const page = useAdminDocumentTypesPage();

  return (
    <PageContainer>
      <AdminDocumentTypesTable page={page} />
      <CreateDocumentTypeDialog page={page} />
      <DeprecateDocumentTypeDialog
        documentType={page.pendingUsage}
        onCancel={page.cancelUsageChange}
        onConfirm={() => void page.confirmUsage()}
        isPending={page.isPending}
      />
      <DeleteDocumentTypeDialog
        documentType={page.pendingDelete}
        onCancel={page.cancelDelete}
        onConfirm={() => void page.confirmDelete()}
        isPending={page.isPending}
      />
    </PageContainer>
  );
}
